import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { COURSES } from "../lib/data";
import { getCourseDetail } from "../lib/courseDetails";
import { useSEO } from "../lib/useSEO";
import { CourseVideo } from "../components/CourseVideo";
import { thumb, thumbWide, fallbackToFull } from "../lib/imageThumb";
import NotFound from "./NotFound";
import { auditionPassee } from "../lib/auditions";
import { usePlanning } from "../lib/usePlanning";
import { courseSlug, seatState, MESSAGE_ESSAI_COMPLET } from "../lib/planningHelpers";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const course = COURSES.find((c) => c.slug === slug);
  const detail = getCourseDetail(slug);
  // Tous les horaires de cette discipline sont complets : pas de cours d'essai (école,
  // 02.11.2026), la carte propose la liste d'attente à la place.
  const { courses: horaires } = usePlanning();
  const groupes = (horaires || []).filter((c) => courseSlug(c) === slug && !seatState(c).hidden);
  const toutComplet = groupes.length > 0 && groupes.every((c) => seatState(c).waitlist);
  useSEO({
    title: course ? `Cours de ${course.name} à Cugy VD — 1000 ARTS` : "Cours introuvable",
    description: course
      ? `${course.name} chez 1000 ARTS à Cugy VD (${course.category}). ${course.desc} Réserve ton cours d'essai en ligne. À 10 min de Lausanne.`
      : "Découvrez nos disciplines de danse à Cugy VD.",
    canonical: course ? `/cours/${slug}` : undefined,
    // Un slug inconnu ne doit pas produire une page indexable pointant vers elle-même.
    robots: course ? undefined : "noindex, follow",
  });

  if (!course) {
    return <NotFound />;
  }

  return (
    <div data-testid="page-course-detail" className="pb-24">
      {/* HERO — image OR video + dark gradient + white text */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        {course.video ? (
          <>
            {/* Fond flouté élégant derrière la vidéo verticale — évite les bandes noires */}
            {course.video.orientation === "vertical" && course.image && (
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${thumb(course.image)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "blur(28px) saturate(1.2) brightness(0.75)",
                  transform: "scale(1.15)",
                }}
              />
            )}
            <CourseVideo
              video={course.video}
              mode="hero"
              title={`${course.name} — 1000 ARTS`}
              wantSound={true}
              posterImage={thumbWide(course.image)}
            />
          </>
        ) : (
          <img loading="eager" fetchPriority="high" decoding="async" src={thumbWide(course.image)} onError={fallbackToFull(course.image)} alt={`${course.name} · ${course.category} · 1000 ARTS Cugy VD`} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(15,27,45,0.65) 0%, rgba(15,27,45,0.30) 55%, rgba(15,27,45,0.05) 100%)" }} />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-12 text-white pointer-events-none">
          <button onClick={() => navigate(-1)} data-testid="course-back" className="self-start mb-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/85 hover:text-white pointer-events-auto">
            <ArrowLeft size={14} /> Retour
          </button>
          <div className="eyebrow mb-3" style={{ color: "#7DE0EA" }}>{course.category}</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] text-white">
            {course.name}
          </h1>
          {detail?.tagline && (
            <p className="mt-6 text-lg md:text-xl text-white/95 max-w-2xl italic">{detail.tagline}</p>
          )}
        </div>
      </section>

      {/* BODY */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 grid lg:grid-cols-3 gap-12">
        {/* Left: text */}
        <div className="lg:col-span-2 space-y-12">
          {detail ? (
            <>
              {detail.callout && (
                <div
                  data-testid="detail-callout"
                  className="rounded-2xl p-5 md:p-6 border-l-4 flex items-start gap-4"
                  style={{ background: "#FFF7ED", borderColor: "#F97316", color: "#7C2D12" }}
                >
                  <span className="text-2xl leading-none shrink-0">⚠️</span>
                  <div className="text-[15px] leading-relaxed">
                    <strong className="block mb-1 text-[#7C2D12]">{detail.callout.title}</strong>
                    <span>{detail.callout.text}</span>
                  </div>
                </div>
              )}

              <div data-testid="detail-intro">
                <div className="eyebrow mb-3">L'esprit du cours</div>
                {detail.intro.map((p, i) => (
                  <p key={i} className="text-lg text-[var(--text)] leading-relaxed mt-4 first:mt-0">{p}</p>
                ))}
              </div>

              <div data-testid="detail-bases">
                <div className="eyebrow mb-6">Les bases travaillées</div>
                <ul className="space-y-3">
                  {detail.bases.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-[var(--text)]">
                      <Check size={18} style={{ color: "var(--primary)", marginTop: 5, flexShrink: 0 }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div data-testid="detail-why">
                <div className="eyebrow mb-6">Pourquoi essayer&nbsp;?</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {detail.why.map((w, i) => (
                    <div key={i} className="card-flat flex items-start gap-3">
                      <Sparkles size={18} style={{ color: "var(--primary)", marginTop: 4, flexShrink: 0 }} />
                      <span className="text-[var(--text)]">{w}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p className="text-lg text-[var(--text)]">{course.desc}</p>
          )}
        </div>

        {/* Right: CTA card */}
        <aside className="lg:sticky lg:top-32 self-start">
          {course.category === "Compétition" ? (
            <div className="card-flat" data-testid="detail-cta-audition-card" style={{ background: "var(--text)", color: "#fff", borderColor: "transparent", padding: "32px 28px" }}>
              <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Accès sur audition</div>
              <h3 className="font-display text-3xl tracking-tight" style={{ color: "#fff" }}>Passe<br /><span style={{ color: "#5EEAD4" }}>l'audition</span></h3>
              <p className="text-sm mt-4" style={{ color: "rgba(255,255,255,0.9)" }}>
                Ce groupe ne se découvre pas en cours d'essai. L'accès se fait par audition, puis
                inscription au semestre pour les danseurs sélectionnés.
              </p>
              <Link
                to="/inscription/audition"
                state={{ groupe: /compagnie/i.test(course.name || "") ? "Compagnie" : "Compétition" }}
                data-testid="detail-cta-audition"
                className="inline-flex items-center gap-2 justify-center mt-6 w-full px-6 py-3 rounded-full font-semibold transition"
                style={{ background: "#fff", color: "var(--text)" }}
              >
                {auditionPassee() ? "Prochaine audition : date à venir" : "S'inscrire à l'audition"} <ArrowRight size={16} />
              </Link>
              <Link
                to="/competition"
                data-testid="detail-cta-competition"
                className="inline-flex items-center gap-2 justify-center mt-3 w-full px-6 py-3 rounded-full font-semibold border transition"
                style={{ borderColor: "rgba(255,255,255,0.5)", color: "#fff" }}
              >
                Découvrir la compétition
              </Link>
            </div>
          ) : course.category === "Cours privés" ? (
            <div className="card-flat" data-testid="detail-cta-prive-card" style={{ background: "var(--primary)", color: "#fff", borderColor: "transparent", padding: "32px 28px" }}>
              <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Réservation en ligne</div>
              <h3 className="font-display text-3xl tracking-tight" style={{ color: "#fff" }}>Réserve ton<br /><span style={{ color: "#E0FBFC" }}>cours privé</span></h3>
              <p className="text-sm mt-4" style={{ color: "rgba(255,255,255,0.9)" }}>
                Choisis ta discipline et ta durée, puis réserve directement le créneau qui t'arrange dans l'agenda.
              </p>
              <Link
                to="/inscription/cours-prive"
                data-testid="detail-cta-cours-prive"
                className="inline-flex items-center gap-2 justify-center mt-6 w-full px-6 py-3 rounded-full font-semibold transition"
                style={{ background: "#fff", color: "var(--primary)" }}
              >
                Réserver mon cours privé <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="detail-cta-contact"
                className="inline-flex items-center gap-2 justify-center mt-3 w-full px-6 py-3 rounded-full font-semibold border transition"
                style={{ borderColor: "rgba(255,255,255,0.5)", color: "#fff" }}
              >
                Poser une question
              </Link>
            </div>
          ) : toutComplet ? (
            <div className="card-flat" data-testid="detail-cta-complet-card" style={{ background: "var(--primary)", color: "#fff", borderColor: "transparent", padding: "32px 28px" }}>
              <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Cours complet</div>
              <h3 className="font-display text-3xl tracking-tight" style={{ color: "#fff" }}>Liste<br /><span style={{ color: "#E0FBFC" }}>d'attente</span></h3>
              <p className="text-sm mt-4" style={{ color: "rgba(255,255,255,0.9)" }} data-testid="detail-essai-complet">
                {MESSAGE_ESSAI_COMPLET}
              </p>
              <Link
                to={`/inscription?type=semestriel${groupes.length === 1 ? `&course=${encodeURIComponent(groupes[0].id)}` : ""}`}
                data-testid="detail-cta-liste-attente"
                className="inline-flex items-center gap-2 justify-center mt-6 w-full px-6 py-3 rounded-full font-semibold transition"
                style={{ background: "#fff", color: "var(--primary)" }}
              >
                S'inscrire en liste d'attente <ArrowRight size={16} />
              </Link>
              <Link
                to="/planning"
                data-testid="detail-cta-planning"
                className="inline-flex items-center gap-2 justify-center mt-3 w-full px-6 py-3 rounded-full font-semibold border transition"
                style={{ borderColor: "rgba(255,255,255,0.5)", color: "#fff" }}
              >
                Voir le planning
              </Link>
            </div>
          ) : (
          <div className="card-flat" style={{ background: "var(--primary)", color: "#fff", borderColor: "transparent", padding: "32px 28px" }}>
            <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Prêt·e à essayer&nbsp;?</div>
            <h3 className="font-display text-3xl tracking-tight" style={{ color: "#fff" }}>Réserve ton<br /><span style={{ color: "#E0FBFC" }}>cours d'essai</span></h3>
            <p className="text-sm mt-4" style={{ color: "rgba(255,255,255,0.9)" }}>
              Réserve ton créneau. On t'aide à trouver le bon niveau si tu hésites.
            </p>
            <Link
              to={`/inscription?cours=${course.slug}`}
              data-testid="detail-cta-inscription"
              className="inline-flex items-center gap-2 justify-center mt-6 w-full px-6 py-3 rounded-full font-semibold transition"
              style={{ background: "#fff", color: "var(--primary)" }}
            >
              S'inscrire <ArrowRight size={16} />
            </Link>
            <Link
              to="/planning"
              data-testid="detail-cta-planning"
              className="inline-flex items-center gap-2 justify-center mt-3 w-full px-6 py-3 rounded-full font-semibold border transition"
              style={{ borderColor: "rgba(255,255,255,0.5)", color: "#fff" }}
            >
              Voir le planning
            </Link>
          </div>
          )}

          <div className="mt-6 text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
            <div className="mb-2">Discipline</div>
            <div className="text-[var(--text)] font-display text-2xl tracking-tight normal-case">{course.category}</div>
          </div>
        </aside>
      </section>

      {/* RELATED — remplacé par un simple bouton pour éviter la mise à jour multiple des photos */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24 text-center">
        <div className="eyebrow mb-3">Explore aussi</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-6">
          D'autres <span style={{ color: "var(--primary)" }}>disciplines</span> t'attendent
        </h2>
        <p className="text-[var(--muted)] max-w-xl mx-auto mb-8">
          Découvre nos {COURSES.length} disciplines : hip-hop, comédie musicale, heels, salsa, ballet, zumba et bien plus.
        </p>
        <Link to="/cours" data-testid="related-cta" className="btn-primary">
          Voir toutes nos disciplines <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
